import keyBy from 'lodash-es/keyBy'

import { useScreen } from '../../../hooks'
import { getQaSelector } from '../../../utils/common'
import {
  detectDragDropPointerCollision,
  DRAG_DROP_SORT_STRATEGY,
  DragDropProvider,
  DragDropSortableItem,
  DragDropSortableList,
} from '../../DragDrop'
import { BIRDVIEW_DRAGGING_ITEM_STYLE } from '../DashboardBirdview.constants'

import {
  BIRDVIEW_TILE_ROW_DEFAULT_COLUMNS,
  BIRDVIEW_TILE_ROW_SORTABLE_ID,
} from './BirdviewTileRow.constants'
import { useBirdviewTileRowOrder } from './BirdviewTileRow.hooks'
import {
  checkIsBirdviewTileRowCarousel,
  getBirdviewTileRowItemId,
} from './BirdviewTileRow.utils'
import { BirdviewTileRowCarousel } from './BirdviewTileRowCarousel'

import * as styles from './BirdviewTileRow.module.css'

// Narrow working areas show a swipe carousel without drag-reorder: the drag
// listeners on a whole tile would hijack the swipe.
function BirdviewTileRow({
  columns = BIRDVIEW_TILE_ROW_DEFAULT_COLUMNS,
  getItemId = getBirdviewTileRowItemId,
  items,
  qaName,
  renderItem,
  storageKeyPrefix,
  withDragHandle = false,
}) {
  const { width } = useScreen()

  const itemById = keyBy(items, getItemId)

  const { handleDragEnd, order } = useBirdviewTileRowOrder({
    defaultOrder: items.map(getItemId),
    storageKeyPrefix,
  })

  const orderedItems = order.map(id => itemById[id]).filter(Boolean)

  function renderOverlay(activeId) {
    const item = itemById[activeId]

    return item ? (
      <div className={styles.overlay}>{renderItem(item)}</div>
    ) : null
  }

  function renderSortableContent(item) {
    return withDragHandle
      ? dragHandleProps => renderItem(item, dragHandleProps)
      : renderItem(item)
  }

  return checkIsBirdviewTileRowCarousel(width) ? (
    <BirdviewTileRowCarousel
      getItemId={getItemId}
      items={orderedItems}
      qaName={qaName}
      renderItem={renderItem}
    />
  ) : (
    <DragDropProvider
      collisionDetection={detectDragDropPointerCollision}
      renderOverlay={renderOverlay}
      onDragEnd={handleDragEnd}
    >
      <DragDropSortableList
        {...getQaSelector({ value: qaName })}
        className={styles.row}
        id={BIRDVIEW_TILE_ROW_SORTABLE_ID}
        items={order}
        strategy={DRAG_DROP_SORT_STRATEGY.grid}
        style={{ '--birdview-tiles-per-row': columns }}
      >
        {orderedItems.map(item => (
          <DragDropSortableItem
            className={styles.item}
            draggingStyle={BIRDVIEW_DRAGGING_ITEM_STYLE}
            id={getItemId(item)}
            key={getItemId(item)}
            withDragHandle={withDragHandle}
          >
            {renderSortableContent(item)}
          </DragDropSortableItem>
        ))}
      </DragDropSortableList>
    </DragDropProvider>
  )
}

export default BirdviewTileRow
